import type { ServerError, ServerParseError } from '@apollo/client/core';
import { ApolloLink, HttpLink } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { Observable } from '@apollo/client/utilities';
import { createConsumer } from '@rails/actioncable';
import { createLazyLoadableLaikaLink } from '@zendesk/laika';
import ActionCableLink from 'graphql-ruby-client/subscriptions/ActionCableLink';
import { generateRequestSignature } from '@/apollo/services/request-signature';
import config from '@/config';
import { handledException } from '@/helpers/datadog';
import { isServicePackageDeletionError } from '@/helpers/service-package';
import { logout } from '@/helpers/session';
import { modal, notification } from '@/helpers/ui';
import { useCompanyStore } from '@/stores/company';
import { useFiskalySystemStore } from '@/stores/fiskaly-system';
import { useLocationsStore } from '@/stores/locations';
import { useUserStore } from '@/stores/user';
import { useVersionMismatchStore } from '@/stores/version-mismatch';
import { GraphqlExecutionError, GraphqlNetworkError } from './errors';

function getCsrfToken(): string {
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  return metaTag?.getAttribute('content') || '';
}

function getPublicCompanyId(): string {
  const { company } = useCompanyStore();
  return company?.publicId ?? '';
}

function getPublicLocationId(): string {
  const { location } = useLocationsStore();
  return location?.publicId ?? '';
}

function getVisitorId(): string {
  const { visitorId } = useUserStore();
  return visitorId ?? '';
}

const extraIdHeaderLink = new ApolloLink((operation: any, forward: any) => {
  const publicCompanyId = getPublicCompanyId();
  const publicLocationId = getPublicLocationId();
  const visitorId = getVisitorId();
  // Resolved per request so an in-app location switch sends that location's system
  const fiskalySystemUuid = publicLocationId
    ? (useFiskalySystemStore().getSystemUuid(publicLocationId) ?? '')
    : '';

  operation.setContext({
    headers: {
      ...operation.getContext().headers,
      'X-Public-Company-Id': publicCompanyId,
      'X-Public-Location-Id': publicLocationId,
      'X-Device-Fingerprint': visitorId,
      'X-Fiskaly-System-Id': fiskalySystemUuid,
    },
  });
  return forward(operation);
});

const authLink = new ApolloLink((operation: any, forward: any) => {
  return new Observable((observer) => {
    generateRequestSignature(operation, getCsrfToken())
      .then(({ signature, timestamp }) => {
        operation.setContext({
          headers: {
            ...operation.getContext().headers,
            'X-Request-Signature': signature,
            'X-Request-Timestamp': timestamp,
          },
        });

        forward(operation).subscribe(observer);
      })
      .catch(() => {
        forward(operation).subscribe(observer);
      });
  });
});

const afterwareLink = new ApolloLink((operation: any, forward: any) =>
  forward(operation).map((response: any) => {
    const context = operation.getContext();
    if (context.response?.headers) {
      const { checkVersionMismatch } = useVersionMismatchStore();
      checkVersionMismatch(context.response.headers.get('version'));
    }
    return response;
  }),
);

const errorLink = onError(
  ({ operation, response, graphQLErrors, networkError }: any) => {
    let statusCode: string;
    if (networkError && 'statusCode' in networkError) {
      statusCode = String(networkError.statusCode);
    } else if (response) {
      statusCode = '200';
    } else {
      statusCode = 'NO_RESPONSE';
    }

    let extensionStatusCode: string;
    if (response?.errors) {
      extensionStatusCode = String(response.errors[0]?.extensions?.code);
    } else {
      extensionStatusCode = '';
    }

    if (statusCode === 'NO_RESPONSE' && !graphQLErrors) {
      notification({ name: 'no-connection' });
      return;
    }

    const operationName = operation?.operationName;

    if (
      operation?.query?.definitions?.some(
        ({ kind, operation }: any) =>
          kind === 'OperationDefinition' && operation === 'subscription',
      )
    ) {
      // Network errors on subscriptions we flatout ignore
      return;
    }

    if (operationName === 'deleteAbsence') {
      // Deleting absence can sometimes trigger a 404 (for an unknown reason), but in this case we don't want to show the error modal
      return;
    }

    if (
      ['deleteServiceGroup', 'updateServiceGroup'].includes(operationName) &&
      isServicePackageDeletionError(graphQLErrors)
    ) {
      return;
    }

    if (networkError && !graphQLErrors) {
      let userSignedIn;

      if ('result' in networkError) {
        userSignedIn = networkError?.result?.user_signed_in;
      }

      if (!userSignedIn && statusCode === '401') {
        notification({ name: 'session-expired' });
        logout();
        return;
      }

      let bodyText: any;
      if ('bodyText' in networkError) {
        bodyText = networkError.bodyText;
      }
      handledException(
        new GraphqlNetworkError({ error: networkError, operationName }),
        {
          operationName,
          responseBody: bodyText,
          responseStatus: statusCode,
          originalError: { ...networkError },
        },
      );
    }

    let modalErrors: (Error | ServerParseError | ServerError)[] = [];

    if (graphQLErrors) {
      modalErrors = [...graphQLErrors];
    }

    if (networkError) {
      modalErrors.push(networkError);
    }

    const modalData = {
      operationName,
      statusCode,
      extensionStatusCode,
      modalErrors,
    };

    if (
      graphQLErrors?.find(
        (error: any) => String(error.extensions?.code) === '404',
      )
    ) {
      modalData.statusCode = '404';
    }

    if (
      graphQLErrors?.find(
        (error: any) =>
          error.extensions?.authorization_module === 'marketing' ||
          (error.extensions?.authorization_module === 'waiting-list' &&
            error.extensions?.code === '401'),
      )
    ) {
      modal('upgrade', { data: modalData });
      return;
    }

    // Exceptions do not rethrow, they ask the user to refresh instead
    if (graphQLErrors) {
      graphQLErrors.forEach((gqlerror: any) => {
        handledException(new GraphqlExecutionError(gqlerror, operationName), {
          operationName,
          responseBody: response?.data,
          responseStatus: statusCode,
          locations: gqlerror.locations,
          query: operation?.query?.loc?.source?.body,
        });
      });
    }

    modal('error', { ...modalData });
  },
);

const httpLink = new HttpLink({
  uri: ({ operationName }: any): string => {
    const authOps = ['signup', 'sendTwoStepLoginSms'];
    return `${config.backendUrl}/api/${authOps.includes(operationName) ? 'graphql_authentication' : 'graphql'}?op=${operationName}`;
  },
  credentials: 'include',
});

let actualLinks;

if (!config.isTest) {
  const cable = createConsumer(config.wsUrl);
  const wsLink = new ActionCableLink({ cable });

  const isSubscription = ({ query: { definitions } }: any): boolean =>
    definitions.some(
      ({ kind, operation }: any) =>
        kind === 'OperationDefinition' && operation === 'subscription',
    );

  const wsWithHeaders = ApolloLink.from([extraIdHeaderLink, wsLink as any]);

  const splitLink = ApolloLink.split(
    isSubscription,
    wsWithHeaders as any,
    httpLink,
  );

  actualLinks = ApolloLink.from([
    errorLink as any,
    extraIdHeaderLink,
    authLink,
    afterwareLink,
    splitLink,
  ]);
} else {
  const laikaLazyLink = createLazyLoadableLaikaLink({
    clientName: 'test-ws-client',
  });
  actualLinks = ApolloLink.from([
    errorLink as any,
    extraIdHeaderLink,
    laikaLazyLink,
    afterwareLink,
    httpLink,
  ]);
}

export const links = actualLinks;
